import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Route as service_route } from '@routes/$service';
import { Route as index_route } from '@routes/index';
import { ArcadeStoreProducts } from '@components/store/ArcadeStoreProducts';
import { buttonVariants, HStack, Text, VStack } from 'ui_2';
import { SponsoredBy } from '@components/sponsor/SponsoredBy';
import { BiLinkExternal } from 'react-icons/bi';
import Header from '@src/components/layout/Header';
import { Page } from '@src/components/layout/Page';
import { ProductShowcaseSlideshow } from '@src/components/smallBusiness/ProductShowcaseSlideshow';
import { useBreakpoints } from '@puzzlehq/frontend-utils';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import {
  AnalyticsEvents,
  useAnalytics,
  useAnalyticsDefaultValues,
} from '@src/providers/analytics';
import { StoreService } from 'jigsaw';
import { useMemo } from 'react';

type BrandDetailsProps = {
  service: StoreService;
  fullWidth?: boolean;
  hidden?: boolean;
};
const BrandDetails = ({
  service,
  fullWidth = false,
  hidden = false,
}: BrandDetailsProps) => {
  const analytics = useAnalytics();
  const { address, network, partBalance } = useAnalyticsDefaultValues();

  return (
    <a
      href={service.url}
      target='_blank'
      onClick={() =>
        analytics.track(AnalyticsEvents.StoreDetailsClicked, {
          service: service.title,
          address,
          network,
          partBalance,
        })
      }
      className={buttonVariants({
        size: 'md',
        variant: 'gray',
        className: `${fullWidth ? 'w-full' : ''} ${hidden ? 'hidden' : ''}`,
      })}
    >
      <HStack className='items-center gap-2'>
        <Text font='regular-xs'>Brand details</Text>
        <BiLinkExternal />
      </HStack>
    </a>
  );
};

type HeaderTitleProps = {
  service: StoreService;
};
const HeaderTitle = ({ service }: HeaderTitleProps) => {
  const { isMedium } = useBreakpoints();

  return (
    <div className='flex flex-row items-center gap-4'>
      <Text font={isMedium ? 'bold-lg' : 'bold-md'}>{service.title}</Text>
      <BrandDetails
        service={service}
        fullWidth={isMedium === false}
        hidden={isMedium === false}
      />
    </div>
  );
};

export const Route = createFileRoute('/$service/')({
  component: function Component() {
    const service = service_route.useLoaderData();
    const analytics = useAnalytics();
    const { address, network, partBalance } = useAnalyticsDefaultValues();

    const { isMedium } = useBreakpoints();

    const previewImageURLs = useMemo(
      () => service.previewImageURLs,
      [JSON.stringify(service.previewImageURLs)]
    );

    return (
      <Page title={service.title} backRoute={index_route.to}>
        <VStack className={`w-full gap-8`}>
          <VStack className='gap-4 '>
            <VStack>
              {previewImageURLs && previewImageURLs.length > 0 && (
                <ProductShowcaseSlideshow images={previewImageURLs} />
              )}
              {service.sponsor && (
                <a
                  href={service.sponsor.url}
                  target='_blank'
                  onClick={() => {
                    if (service.sponsor?.url) {
                      void analytics.track(AnalyticsEvents.AdBannerClicked, {
                        service: service.title,
                        sponsor: service.sponsor.name,
                        address,
                        network,
                        partBalance,
                      });
                    }
                  }}
                >
                  <VStack
                    className={`w-full items-center ${
                      service.sponsor.splashPageTextColor === 'dark'
                        ? 'bg-white'
                        : 'bg-black'
                    } py-2`}
                  >
                    {service.sponsor.logoURL ? (
                      <SponsoredBy
                        logoURL={service.sponsor.logoURL}
                        textColor={service.sponsor.splashPageTextColor}
                        service={service.title}
                      />
                    ) : (
                      <VStack className='text-center'>
                        <Text font='xs'>Sponsored By</Text>
                        <Text font='sm'>{service.sponsor.name}</Text>
                      </VStack>
                    )}
                  </VStack>
                </a>
              )}
            </VStack>
            <BrandDetails
              service={service}
              fullWidth={isMedium}
              hidden={isMedium}
            />
          </VStack>
          <VStack className='items-center'>
            <ArcadeStoreProducts
              serviceSlug={service.slug}
              serviceTitle={service.title}
            />
          </VStack>
        </VStack>
      </Page>
    );
  },
});
